import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useTranslations } from 'context/TranslationContext';

import { CONFIDENCE_STATUS_COLORS } from 'utils/constants/confidenceStatusColors';
import { ConfidenceLevel, SectionStatus } from 'utils/enums/Confidence';
import { getSectionStatus } from 'utils/helpers/confidenceHelpers';

import { cardContentStyle, cardHeaderStyle } from 'styles/shared/CardStyle';

import { CardSummary } from './CardSummary';
import { ConfidenceIcon } from './ConfidenceIcon';

const SECTION_STATUS_ICON_LEVEL: Record<SectionStatus, ConfidenceLevel | null> =
  {
    [SectionStatus.ERROR]: ConfidenceLevel.LOW,
    [SectionStatus.LOW_CONFIDENCE]: ConfidenceLevel.LOW,
    [SectionStatus.NEEDS_REVIEW]: ConfidenceLevel.MEDIUM,
    [SectionStatus.NONE]: null
  };

interface InvoiceVerificationCardProps {
  title: string;
  children: ReactNode;
  confidenceLevel?: ConfidenceLevel;
  summary?: string;
  summaryEqualWidths?: boolean;
  headerStartAdornment?: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
  defaultExpanded?: boolean;
  hasError?: boolean;
}

export const InvoiceVerificationCard = ({
  title,
  children,
  confidenceLevel,
  summary,
  summaryEqualWidths = false,
  headerStartAdornment,
  expanded: controlledExpanded,
  onToggle,
  onExpanded,
  autoExpandReason,
  defaultExpanded = true,
  hasError = false
}: InvoiceVerificationCardProps) => {
  const { translate } = useTranslations();
  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const toggleExpanded = () => {
    if (isControlled && onToggle) {
      onToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  useEffect(() => {
    if (isControlled && controlledExpanded && autoExpandReason) {
      const liveRegion = document.getElementById(
        'invoice-verification-live-region'
      );
      if (liveRegion) {
        liveRegion.textContent = autoExpandReason;
      }
    }
  }, [isControlled, controlledExpanded, autoExpandReason]);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const status = getSectionStatus(
    confidenceLevel ?? ConfidenceLevel.UNKNOWN,
    hasError
  );

  const borderColor = (() => {
    switch (status) {
      case SectionStatus.ERROR:
        return CONFIDENCE_STATUS_COLORS.missingData.main;
      case SectionStatus.LOW_CONFIDENCE:
        return CONFIDENCE_STATUS_COLORS.lowConfidence.main;
      case SectionStatus.NEEDS_REVIEW:
        return CONFIDENCE_STATUS_COLORS.needsReview.main;
      default:
        return '#e5e7eb';
    }
  })();

  const bannerConfig = (() => {
    switch (status) {
      case SectionStatus.ERROR:
        return {
          label: translate('messages.requiredDataMissing'),
          backgroundColor: CONFIDENCE_STATUS_COLORS.missingData.background,
          color: CONFIDENCE_STATUS_COLORS.missingData.text
        };
      case SectionStatus.LOW_CONFIDENCE:
        return {
          label: translate('messages.lowConfidence'),
          backgroundColor: CONFIDENCE_STATUS_COLORS.lowConfidence.background,
          color: CONFIDENCE_STATUS_COLORS.lowConfidence.text
        };
      case SectionStatus.NEEDS_REVIEW:
        return {
          label: translate('messages.needsReview'),
          backgroundColor: CONFIDENCE_STATUS_COLORS.needsReview.background,
          color: CONFIDENCE_STATUS_COLORS.needsReview.text
        };
      default:
        return null;
    }
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '1rem',
        border: `1px solid ${borderColor}`,
        borderLeft: `2px solid ${borderColor}`
      }}
    >
      <Box
        sx={{
          ...cardHeaderStyle,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #f0f0f0' : 'none',
          borderRadius: isExpanded ? '16px 16px 0 0' : '16px'
        }}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={
          autoExpandReason
            ? `${title}: ${autoExpandReason}`
            : `${title}: ${isExpanded ? 'expanded' : 'collapsed'}`
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {headerStartAdornment}
          {!headerStartAdornment &&
            (status !== SectionStatus.NONE ||
              (confidenceLevel !== undefined &&
                confidenceLevel !== ConfidenceLevel.UNKNOWN)) && (
              <ConfidenceIcon
                level={
                  SECTION_STATUS_ICON_LEVEL[status] ??
                  confidenceLevel ??
                  ConfidenceLevel.LOW
                }
                sectionStatus={status}
              />
            )}
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              flexShrink: 0
            }}
          >
            {title}
          </Typography>
        </Box>

        {!isExpanded && summary && (
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <CardSummary
              summary={summary}
              testId="invoice-verification-summary"
              equalWidths={summaryEqualWidths}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {bannerConfig && (
            <Chip
              label={bannerConfig.label}
              sx={{
                backgroundColor: bannerConfig.backgroundColor,
                color: bannerConfig.color,
                fontSize: '0.75rem',
                fontWeight: 600,
                height: '1.5rem',
                px: 1
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded();
            }}
            aria-label={isExpanded ? 'collapse' : 'expand'}
            aria-expanded={isExpanded}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={isExpanded}
        timeout={prefersReducedMotion ? 0 : 'auto'}
        appear={!prefersReducedMotion && !isControlled}
        onEntered={onExpanded}
      >
        <Box sx={cardContentStyle}>{children}</Box>
      </Collapse>
    </Paper>
  );
};
